package com.evoapps.evoshift.domain.model

/*
 * SH-211: a presentation in a course page ([CoursePageKind.PRESENTATION]). Either a link to a
 * presentation online (Google Slides, Prezi, PowerPoint on OneDrive / Office 365 or any public
 * .pptx / .odp address) that the web app shows in a frame from the provider's embed address, or a
 * file the manager uploads (PowerPoint .pptx, Impress .odp or a PDF, at most 50 MB) to the
 * workplace's Storage folder, readable only by the people working there (storage.rules). An
 * uploaded file is never handed to an outside viewer (the Office web viewer needs a public file):
 * the app downloads it with the person's sign-in and opens it in the device's own viewer; a PDF
 * (the upload itself or a PDF export the manager adds next to a .pptx / .odp) is also shown in
 * the course on the web. docs/training.md "Presentations".
 */

enum class PresentationSource {
    /** Google Slides (or a presentation file in Google Drive). */
    GOOGLE_SLIDES,
    PREZI,
    /** PowerPoint on OneDrive / Office 365, or a public .pptx / .odp file through the Office viewer. */
    OFFICE,
    /** A file uploaded to Storage. */
    UPLOAD
}

data class CoursePresentation(
    val source: PresentationSource,
    /** Links: the address to open in the browser (https). */
    val link: String? = null,
    /** Links: the provider's embed address for a frame; null when the provider allows none (open only). */
    val embedUrl: String? = null,
    /** Uploads: the file in Storage ([PresentationFiles.path]). */
    val filePath: String? = null,
    val fileName: String? = null,
    /** The uploaded file's type ([PresentationFiles.TYPES]). */
    val fileType: String? = null,
    /** A PDF export of the uploaded .pptx / .odp, shown in the course on the web. */
    val pdfPath: String? = null,
    val pdfName: String? = null
) {
    val isUpload: Boolean get() = source == PresentationSource.UPLOAD

    /** The PDF to show inline: the upload itself when it is one, else its PDF export. */
    val inlinePdfPath: String? get() = if (fileType == DocumentFiles.PDF) filePath else pdfPath

    /** Storage paths this presentation keeps (removed with the course). */
    val storedPaths: List<String> get() = listOfNotNull(filePath, pdfPath)

    /** Something to show: a link, or an uploaded file. */
    val isComplete: Boolean get() = if (isUpload) filePath != null else link != null
}

/** Why a link is not accepted as a presentation. */
enum class PresentationLinkProblem {
    /** Not an https address. */
    NOT_HTTPS,
    /** A site the app does not know presentations from. */
    UNSUPPORTED_SITE,
    /** The site's address, but not of a presentation (a document, a folder, a profile). */
    NOT_A_PRESENTATION
}

/** The result of reading a link: the presentation, or why not. */
sealed interface PresentationLinkResult {
    data class Accepted(val presentation: CoursePresentation) : PresentationLinkResult
    data class Refused(val problem: PresentationLinkProblem) : PresentationLinkResult
}

/**
 * Reading presentation links into [CoursePresentation]s with the provider's embed address. No
 * regular expressions: the address is split into host, path segments and query by hand.
 */
object PresentationLinks {
    /** Hosts the web app's frame-src (firebase.json) allows: keep them in step. */
    val EMBED_HOSTS: Set<String> = setOf(
        "docs.google.com", "drive.google.com", "prezi.com", "onedrive.live.com", "view.officeapps.live.com"
    )

    private data class Url(val host: String, val segments: List<String>, val query: List<Pair<String, String>>, val raw: String) {
        fun param(name: String): String? = query.firstOrNull { it.first.equals(name, ignoreCase = true) }?.second
    }

    private fun parse(raw: String): Url? {
        val value = raw.trim()
        if (value.length > CourseRules.MAX_URL || value.any { it.isWhitespace() || it.isISOControl() || it in "<>\"`\\" }) return null
        val withScheme = if ("://" in value) value else "https://$value"
        if (!withScheme.startsWith("https://", ignoreCase = true)) return null
        val rest = withScheme.substring(8)
        val authority = rest.substringBefore('/').substringBefore('?').substringBefore('#')
        if (authority.isEmpty() || '@' in authority || ':' in authority) return null
        val host = authority.lowercase()
        if ('.' !in host || !host.all { it.isLetterOrDigit() || it == '.' || it == '-' }) return null
        val afterHost = rest.substring(authority.length).substringBefore('#')
        val path = afterHost.substringBefore('?')
        val query = afterHost.substringAfter('?', "").split('&').filter { it.isNotEmpty() }.map {
            it.substringBefore('=') to it.substringAfter('=', "")
        }
        return Url(host, path.split('/').filter { it.isNotEmpty() }, query, "https://$host$afterHost")
    }

    private fun isId(value: String?, min: Int = 6): Boolean =
        value != null && value.length in min..200 && value.all { it.isLetterOrDigit() || it == '_' || it == '-' }

    private fun accepted(source: PresentationSource, link: String, embed: String?) =
        PresentationLinkResult.Accepted(CoursePresentation(source = source, link = link, embedUrl = embed))

    private fun refused(problem: PresentationLinkProblem) = PresentationLinkResult.Refused(problem)

    /** [raw] (what the manager pasted) as a presentation, or why it is refused. */
    fun read(raw: String): PresentationLinkResult {
        val trimmed = raw.trim()
        if (trimmed.startsWith("http://", ignoreCase = true)) return refused(PresentationLinkProblem.NOT_HTTPS)
        if ("://" in trimmed && !trimmed.startsWith("https://", ignoreCase = true)) return refused(PresentationLinkProblem.NOT_HTTPS)
        val url = parse(trimmed) ?: return refused(PresentationLinkProblem.NOT_HTTPS)
        return when {
            url.host == "docs.google.com" -> googleSlides(url)
            url.host == "drive.google.com" -> googleDrive(url)
            url.host == "prezi.com" || url.host == "www.prezi.com" -> prezi(url)
            url.host == "onedrive.live.com" -> oneDrive(url)
            url.host == "1drv.ms" -> if (url.segments.size >= 2) accepted(PresentationSource.OFFICE, url.raw, null) else refused(PresentationLinkProblem.NOT_A_PRESENTATION)
            url.host == "view.officeapps.live.com" -> officeViewer(url)
            url.host.endsWith(".sharepoint.com") || url.host == "office.com" || url.host == "www.office.com" ->
                // Office 365 (OneDrive for work, SharePoint): open in the browser, where the person signs in.
                if (url.segments.isNotEmpty()) accepted(PresentationSource.OFFICE, url.raw, null) else refused(PresentationLinkProblem.NOT_A_PRESENTATION)
            isPresentationFile(url.segments.lastOrNull()) -> {
                val src = encode(url.raw)
                accepted(
                    PresentationSource.OFFICE,
                    url.raw,
                    "https://view.officeapps.live.com/op/embed.aspx?src=$src"
                )
            }
            else -> refused(PresentationLinkProblem.UNSUPPORTED_SITE)
        }
    }

    /** /presentation/d/{id}/…, /presentation/u/0/d/{id}/…, published /presentation/d/e/{id}/pub. */
    private fun googleSlides(url: Url): PresentationLinkResult {
        val s = url.segments
        if (s.firstOrNull() != "presentation") return refused(PresentationLinkProblem.NOT_A_PRESENTATION)
        val d = s.indexOf("d")
        if (d < 0) return refused(PresentationLinkProblem.NOT_A_PRESENTATION)
        val base = "https://docs.google.com/presentation/d/"
        val embedQuery = "embed?start=false&loop=false&delayms=3000"
        return if (s.getOrNull(d + 1) == "e") {
            val id = s.getOrNull(d + 2)
            if (!isId(id, 10)) refused(PresentationLinkProblem.NOT_A_PRESENTATION)
            else accepted(PresentationSource.GOOGLE_SLIDES, "${base}e/$id/pub", "${base}e/$id/$embedQuery")
        } else {
            val id = s.getOrNull(d + 1)
            if (!isId(id, 10)) refused(PresentationLinkProblem.NOT_A_PRESENTATION)
            else accepted(PresentationSource.GOOGLE_SLIDES, "$base$id/edit?usp=sharing", "$base$id/$embedQuery")
        }
    }

    /** A presentation file in Drive: /file/d/{id}/view → /file/d/{id}/preview. */
    private fun googleDrive(url: Url): PresentationLinkResult {
        val s = url.segments
        val id = if (s.getOrNull(0) == "file" && s.getOrNull(1) == "d") s.getOrNull(2) else null
        if (!isId(id, 10)) return refused(PresentationLinkProblem.NOT_A_PRESENTATION)
        return accepted(PresentationSource.GOOGLE_SLIDES, "https://drive.google.com/file/d/$id/view", "https://drive.google.com/file/d/$id/preview")
    }

    private val preziReserved = setOf("view", "embed", "p", "v", "i", "dashboard", "pricing", "login", "signup", "explore", "support", "features", "user")

    /**
     * Prezi Present /p/{id}/… → /p/embed/{id}/, Prezi Video /v/{id}/… → /v/embed/{id}/, classic
     * /{id}/{title}/ → /embed/{id}/; embed addresses as they are. A /view/… share link opens only.
     */
    private fun prezi(url: Url): PresentationLinkResult {
        val s = url.segments
        val first = s.firstOrNull() ?: return refused(PresentationLinkProblem.NOT_A_PRESENTATION)
        val open = "https://prezi.com/" + s.joinToString("/") + "/"
        return when {
            (first == "p" || first == "v") && s.getOrNull(1) == "embed" && isId(s.getOrNull(2)) ->
                accepted(PresentationSource.PREZI, "https://prezi.com/$first/${s[2]}/", "https://prezi.com/$first/embed/${s[2]}/")
            (first == "p" || first == "v") && isId(s.getOrNull(1)) ->
                accepted(PresentationSource.PREZI, open, "https://prezi.com/$first/embed/${s[1]}/")
            first == "embed" && isId(s.getOrNull(1)) ->
                accepted(PresentationSource.PREZI, "https://prezi.com/${s[1]}/", "https://prezi.com/embed/${s[1]}/")
            first == "view" && isId(s.getOrNull(1)) -> accepted(PresentationSource.PREZI, open, null)
            first !in preziReserved && isId(first, 8) -> accepted(PresentationSource.PREZI, open, "https://prezi.com/embed/$first/")
            else -> refused(PresentationLinkProblem.NOT_A_PRESENTATION)
        }
    }

    /** OneDrive (personal): any address naming the file (resid / id, cid, authkey) → /embed. */
    private fun oneDrive(url: Url): PresentationLinkResult {
        val keep = listOf("resid", "cid", "id", "authkey", "ithint")
        val params = keep.mapNotNull { name -> url.param(name)?.takeIf { it.isNotEmpty() && it.all(::isQueryChar) }?.let { name to it } }
        if (params.none { it.first == "resid" || it.first == "id" }) return refused(PresentationLinkProblem.NOT_A_PRESENTATION)
        val query = (params + ("em" to "2")).joinToString("&") { "${it.first}=${it.second}" }
        return accepted(PresentationSource.OFFICE, url.raw, "https://onedrive.live.com/embed?$query")
    }

    /** The Office viewer's own addresses: op/view.aspx or op/embed.aspx with a public https src. */
    private fun officeViewer(url: Url): PresentationLinkResult {
        val page = url.segments.lastOrNull()?.lowercase()
        val src = url.param("src")
        if (url.segments.firstOrNull() != "op" || (page != "view.aspx" && page != "embed.aspx") || src.isNullOrEmpty() || !src.all(::isQueryChar)) {
            return refused(PresentationLinkProblem.NOT_A_PRESENTATION)
        }
        val decoded = decode(src)
        if (!decoded.startsWith("https://", ignoreCase = true)) return refused(PresentationLinkProblem.NOT_HTTPS)
        return accepted(
            PresentationSource.OFFICE,
            "https://view.officeapps.live.com/op/view.aspx?src=$src",
            "https://view.officeapps.live.com/op/embed.aspx?src=$src"
        )
    }

    private fun isQueryChar(c: Char) = c.isLetterOrDigit() || c in "-_.~%!*'()"

    private fun isPresentationFile(name: String?): Boolean {
        val ext = name?.substringAfterLast('.', "")?.lowercase() ?: return false
        return ext in setOf("pptx", "ppt", "ppsx", "pps", "odp")
    }

    private const val HEX = "0123456789ABCDEF"

    /** Percent-encodes [value] for a query parameter (RFC 3986 unreserved characters stay). */
    fun encode(value: String): String = buildString {
        value.encodeToByteArray().forEach { byte ->
            val c = byte.toInt() and 0xFF
            val ch = c.toChar()
            if (c < 0x80 && (ch.isLetterOrDigit() || ch in "-_.~")) append(ch)
            else append('%').append(HEX[c shr 4]).append(HEX[c and 0x0F])
        }
    }

    private fun decode(value: String): String {
        val bytes = mutableListOf<Byte>()
        var i = 0
        while (i < value.length) {
            val c = value[i]
            if (c == '%' && i + 2 < value.length) {
                val hex = value.substring(i + 1, i + 3).toIntOrNull(16)
                if (hex != null) { bytes += hex.toByte(); i += 3; continue }
            }
            value[i].toString().encodeToByteArray().forEach { bytes += it }
            i++
        }
        return bytes.toByteArray().decodeToString()
    }
}

/** Uploaded presentation files in Storage (storage.rules: managers write, members read). */
object PresentationFiles {
    const val MAX_BYTES: Int = 50 * 1024 * 1024
    const val PPTX = "application/vnd.openxmlformats-officedocument.presentationml.presentation"
    const val ODP = "application/vnd.oasis.opendocument.presentation"
    val TYPES: Set<String> = setOf(PPTX, ODP, DocumentFiles.PDF)

    fun path(workplaceId: String, courseId: String, fileId: String): String {
        listOf(workplaceId, courseId, fileId).forEach { require(it.isNotBlank() && '/' !in it) { "Invalid path segment" } }
        return "workplaces/$workplaceId/courses/$courseId/slides/$fileId"
    }

    /** What is wrong with [bytes] as a presentation (or with [pdfOnly], as its PDF export), or null. */
    fun problem(bytes: ByteArray, pdfOnly: Boolean = false): DocumentProblem? = when {
        bytes.isEmpty() -> DocumentProblem.EMPTY
        bytes.size > MAX_BYTES -> DocumentProblem.TOO_LARGE
        DocumentFiles.sniff(bytes).let { it == null || it !in (if (pdfOnly) setOf(DocumentFiles.PDF) else TYPES) } -> DocumentProblem.UNSUPPORTED_TYPE
        else -> null
    }
}
